import React from "react";
import { Box } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * PageHeader — consistent header at the top of each page.
 *
 * Props:
 *   title:    string — H1
 *   subtitle: string — 14/400 slate-500 under the title
 *   actions:  ReactNode — right-aligned button row
 *   tabs:     [{ label, to?, active?, onClick? }] — underline tabs row (optional)
 *   maxWidth: max width of the content row (number px or "full"). Default 1280.
 */

function PageHeader({ title, subtitle, actions, tabs, maxWidth = 1280 }) {
  const navigate = useNavigate();
  const location = useLocation();
  const containerStyle =
    maxWidth === "full"
      ? { width: "100%" }
      : { width: "100%", maxWidth, mx: "auto" };

  const hasTabs = Array.isArray(tabs) && tabs.length > 0;

  return (
    <Box
      sx={{
        backgroundColor: "#ffffff",
        borderBottom: hasTabs ? "none" : "1px solid #e2e8f0",
      }}
    >
      <Box sx={{ ...containerStyle, px: { xs: "16px", md: "32px" }, pt: "24px", pb: hasTabs ? "0" : "24px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: "16px",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {title && (
              <Box
                component="h1"
                sx={{
                  m: 0,
                  fontSize: 24,
                  fontWeight: 600,
                  color: "#0f172a",
                  lineHeight: 1.25,
                  letterSpacing: "-0.01em",
                }}
              >
                {title}
              </Box>
            )}
            {subtitle && (
              <Box
                sx={{
                  mt: "4px",
                  fontSize: 14,
                  fontWeight: 400,
                  color: "#64748b",
                  lineHeight: 1.5,
                }}
              >
                {subtitle}
              </Box>
            )}
          </Box>
          {actions && (
            <Box sx={{ display: "flex", gap: "8px", flexShrink: 0 }}>
              {actions}
            </Box>
          )}
        </Box>

        {hasTabs && (
          <Box
            role="tablist"
            sx={{
              mt: "16px",
              display: "flex",
              gap: "4px",
              borderBottom: "1px solid #e2e8f0",
              overflowX: "auto",
            }}
          >
            {tabs.map((tab) => {
              const active =
                tab.active != null
                  ? tab.active
                  : tab.to
                  ? location.pathname === tab.to ||
                    (tab.to !== "/" && location.pathname.startsWith(tab.to))
                  : false;
              return (
                <Box
                  key={tab.label}
                  role="tab"
                  aria-selected={active}
                  tabIndex={0}
                  onClick={() => {
                    if (tab.onClick) tab.onClick();
                    else if (tab.to) navigate(tab.to);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      if (tab.onClick) tab.onClick();
                      else if (tab.to) navigate(tab.to);
                    }
                  }}
                  sx={{
                    height: 36,
                    px: "12px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                    color: active ? "#0f172a" : "#64748b",
                    borderBottom: active ? "2px solid #f97316" : "2px solid transparent",
                    marginBottom: "-1px",
                    transition: "color 150ms ease-out, border-color 150ms ease-out",
                    whiteSpace: "nowrap",
                    "&:hover": { color: "#0f172a" },
                    "&:focus-visible": { outline: "2px solid #f97316", outlineOffset: 2 },
                  }}
                >
                  {tab.label}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default PageHeader;
